import { Box, Table, TableBody, TableRow, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { SORT_DIRECTION } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';

import { IPublicationList } from '../../AssemblyPublicationPage.interface';
import {
  ArrowIcon,
  CustomTableCell,
  CustomTableHead,
  CustomTableRow,
  InViewBoxContainer,
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
  SortIcons,
  TableBodyContainer,
  TableHeadCell,
} from '../../AssemblyPublicationPage.styled';
import {
  AssemblyPublicationTableProps,
  IAssemblyPublicationData,
} from './AssemblyPublicationTable.interface';

export const AssemblyPublicationTable = (
  props: AssemblyPublicationTableProps,
) => {
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const tableRef = useRef<HTMLDivElement>(null);
  const {
    isLoading,
    getNextListPublicationPage,
    columns,
    listPublication,
    hasNextPage,
    handleSort,
    activeColumn,
    sorting,
    activeRow,
    setActiveRow,
    hasData,
    activeTab,
  } = props;

  useEffect(() => {
    if (inView) {
      getNextListPublicationPage();
    }
  }, [getNextListPublicationPage, inView]);

  const handleSelectRow = (id: number, pageIndex: number) => {
    if (!activeTab) {
      setActiveRow(`${id} - ${pageIndex} - ${activeTab}`);
    }
  };

  if (isLoading) {
    return <Progress />;
  }

  return (
    <>
      <div ref={tableRef}>
        <TableBodyContainer>
          <Table stickyHeader>
            <CustomTableHead>
              <TableRow>
                {columns.map((column) => (
                  <CustomTableCell key={column.key} width={column.width}>
                    <TableHeadCell>
                      {column.label}
                      {column.sortIcon && (
                        <SortIcons>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.ASC)
                            }>
                            <KeyboardArrowUpIconStyled
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.ASC
                              }
                            />
                          </ArrowIcon>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.DESC)
                            }>
                            <KeyboardArrowDownIconStyled
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.DESC
                              }
                            />
                          </ArrowIcon>
                        </SortIcons>
                      )}
                    </TableHeadCell>
                  </CustomTableCell>
                ))}
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {!isLoading &&
                listPublication?.map(
                  (row: IAssemblyPublicationData, pageIndex: number) => (
                    <CustomTableRow
                      key={row.id}
                      active={
                        activeRow === `${row.id} - ${pageIndex} - ${activeTab}`
                      }
                      onClick={() => handleSelectRow(row.id, pageIndex)}>
                      {columns.map((column, index) => (
                        <CustomTableCell
                          key={column.key}
                          active={
                            index === 0 &&
                            activeRow ===
                              `${row.id} - ${pageIndex} - ${activeTab}`
                          }>
                          <CustomTooltip
                            width={250}
                            placement="bottom"
                            title={row[column.key as keyof IPublicationList]}
                            overflowLineNumber={2}>
                            <Typography>
                              {row[column.key as keyof IPublicationList]}
                            </Typography>
                          </CustomTooltip>
                        </CustomTableCell>
                      ))}
                    </CustomTableRow>
                  ),
                )}
            </TableBody>
          </Table>
          {!hasData && <NoResultAlert />}
          {hasNextPage && (
            <InViewBoxContainer>
              <Box ref={inViewRef} />
            </InViewBoxContainer>
          )}
        </TableBodyContainer>
      </div>
    </>
  );
};
